import { useAppTranslation } from '@flowus/common/i18n';
import { cx } from '@flowus/common/cx';
import { useEffect, type FC, type MouseEvent } from 'react';
import { Icon } from 'src/common/components/icon';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { judgeSharePage, useGetPageId } from 'src/utils/getPageId';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import {
  addWikiSuccess,
  removeWikiSuccessMessage,
  WikiList,
} from 'src/views/main/aside/wiki/wiki-list';
import { WikiManager } from 'src/redux/managers/block/wiki-manager';
import { usePersonalWikiId } from 'src/views/main/aside/wiki/use-personal-wiki-id';
import { transaction } from 'src/hooks/use-transaction';
import { setBlockPermission } from 'src/redux/managers/block/update-permission';
import { PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import { getRolePermissionRole } from 'src/hooks/share/use-permission-utils';
import { message } from '@flowus/common/components/message';
import { request } from 'src/common/request';
import { findFirstAllowSubscribePage } from 'src/utils/block-utils';
import { addSubscribe, splicePathSearchParams } from '@flowus/common';
import { useCurrentUser } from 'src/hooks/user';
import { useIsNeedPay } from 'src/hooks/page/use-page-meta';
import { useBlock } from 'src/hooks/block/use-block';
import { bizTracker } from 'src/utils/biz-tracker';
import { SearchParams, ViewPath } from 'src/utils';
import { useHistory } from 'react-router-dom';
import { cache } from 'src/redux/store';
import { useFetchPage } from 'src/hooks/page';
import { setSubscribeStatus, useSubscribeStatus } from 'src/hooks/wiki/store';

interface Props {
  uuid?: string;
  className?: string;
}

export const WikiButton: FC<Props> = ({ uuid, className }) => {
  const openModal = useOpenModal();
  const pageId = useGetPageId();
  const id = uuid || pageId;
  const block = useBlock(id);
  const isLogin = useIsLogin();
  const { shared, role, allowSubscribe } = usePermissions(id);
  const isSharePage = judgeSharePage();
  const personalWikiId = usePersonalWikiId();
  const personalWikiBlock = useBlock(personalWikiId);
  const currentUser = useCurrentUser();
  const isNeedPay = useIsNeedPay(pageId);
  const history = useHistory();
  const fetchPage = useFetchPage();
  const subscribed = useSubscribeStatus(id);
  const { t } = useAppTranslation();
  const isSelected =
    subscribed ||
    !!personalWikiBlock?.subNodes.find((v) => {
      return cache.blocks[v]?.data.ref?.uuid === id;
    });

  const isBlockHasWiki = !!personalWikiBlock?.subNodes.find((v) => {
    return cache.blocks[v]?.data.ref?.uuid === id;
  });
  useEffect(() => {
    personalWikiId && void fetchPage(personalWikiId);
  }, [fetchPage, personalWikiId]);

  const updateFromWiki = (wikiId: string, pageId: string) => {
    if (WikiManager.addReferencePage(wikiId, pageId)) {
      !isSharePage &&
        addWikiSuccess(t('components.share.wiki_button.added_to_knowledge_base'), wikiId, pageId);
    } else {
      removeWikiSuccessMessage();
      WikiManager.removeReferencePage(wikiId, pageId);
      !isSharePage &&
        message.success({
          content: t('common.removed_from_wiki'),
        });
    }
  };

  useEffect(() => {
    // 刚新创建的页面会调用这个接口然后报错，这里做个时间的校验
    if (isLogin && block?.createdAt && Date.now() - block.createdAt > 5 * 1000) {
      void request.infra.getSubscribeStatus(id).then((subscribed) => {
        setSubscribeStatus(id, subscribed);
      });
    }
  }, [id, isLogin, block?.createdAt]);

  if (!block) return null;
  if (!allowSubscribe && isSharePage) return null;

  if (isSelected && isSharePage) {
    return null;
  }

  const openMenu = async (event: MouseEvent<HTMLElement>) => {
    if (!isLogin) {
      history.push(
        splicePathSearchParams(ViewPath.login, {
          key: SearchParams.callbackPath,
          value: location.href,
        })
      );
      return;
    }

    if (isSharePage) {
      setSubscribeStatus(pageId, !isSelected);
      // 兼容以前的订阅数据
      const targetId = findFirstAllowSubscribePage(pageId) ?? pageId;
      await addSubscribe(
        {
          targetId,
          subscribed: !isSelected,
          addSubscribe: request.infra.addSubscribe,
          cancelSubscribe: request.infra.cancelSubscribe,
          user: currentUser,
          openDialog: openModal.warning,
          callback: () => {},
        },
        {
          pageId,
          needPay: isNeedPay,
          userId: currentUser.uuid,
          createdBy: block?.createdBy,
          spaceId: block?.spaceId,
          event: bizTracker.event,
        }
      );
      if (isSelected) {
        removeWikiSuccessMessage();
        message.success({
          content: t('common.unsubscribe_and_remove'),
          key: pageId,
        });
      } else {
        personalWikiId &&
          addWikiSuccess(
            t('components.share.wiki_button.subscribe_and_add'),
            personalWikiId,
            pageId
          );
      }
      if (isBlockHasWiki && personalWikiId) {
        updateFromWiki(personalWikiId, pageId);
      }
      return;
    }

    openModal.dropdown({
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <WikiList
          pageId={id}
          onDimiss={() => {
            onCloseModal();
          }}
          onSelectWiki={(wikiId) => {
            onCloseModal();
            updateFromWiki(wikiId, pageId);
          }}
        />
      ),
    });
  };

  return (
    <div
      {...bindDataTestId(TestIds.driveFileShare)}
      onClick={openMenu}
      className={cx('flex items-center justify-center rounded animate-hover p-1', className)}
    >
      <Tooltip
        popup={
          isSharePage
            ? isSelected
              ? t('common.unsubscribe_and_remove')
              : t('components.share.wiki_button.subscribe_and_add')
            : t('components.share.wiki_button.add_to_knowledge_base')
        }
      >
        <Icon size="middle" name="IcKnowledgeBase" />
      </Tooltip>
    </div>
  );
};
